import { createFileRoute, Link } from "@tanstack/react-router";
import { STATES_DATA, slugify } from "@/data/states";

export const Route = createFileRoute("/sitemap")({
  head: () => ({
    meta: [
      { title: "Site Map — Vending Machine Free.com" },
      {
        name: "description",
        content:
          "HTML site map for Vending Machine Free.com — browse every state and major city page for free vending machine placement across the USA.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Site Map — Vending Machine Free.com" },
      { property: "og:url", content: "https://vendingmachinefree.com/sitemap" },
    ],
    links: [{ rel: "canonical", href: "https://vendingmachinefree.com/sitemap" }],
  }),
  component: SitemapPage,
});

function SitemapPage() {
  const sorted = STATES_DATA.slice().sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <nav className="flex gap-4 text-sm">
            <Link to="/" className="hover:text-safety">
              Home
            </Link>
            <Link to="/about" className="hover:text-safety">
              About
            </Link>
            <Link to="/coverage" className="hover:text-safety">
              Coverage
            </Link>
            <Link to="/contact" className="hover:text-safety">
              Apply
            </Link>
          </nav>
        </div>
      </header>

      <section className="py-12">
        <div className="container-x">
          <h1 className="font-display text-4xl uppercase">Site Map</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Full crawl index of every page on Vending Machine Free.com. Also see our{" "}
            <a href="/sitemap-index.xml" className="text-safety underline">
              XML sitemap index
            </a>{" "}
            and{" "}
            <a href="/sitemap.xml" className="text-safety underline">
              sitemap.xml
            </a>
            .
          </p>

          <h2 className="mt-10 font-display text-2xl uppercase">Main Pages</h2>
          <ul className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <li>
              <Link to="/" className="hover:text-safety">
                Home
              </Link>
            </li>
            <li>
              <Link to="/about" className="hover:text-safety">
                About Us
              </Link>
            </li>
            <li>
              <a href="/free-vending-machines-usa" className="hover:text-safety">
                Free Vending Machines USA
              </a>
            </li>
            <li>
              <a href="/free-vending-machine-placement" className="hover:text-safety">
                Free Vending Placement
              </a>
            </li>
            <li>
              <a href="/vending-machine-placement" className="hover:text-safety">
                Vending Placement
              </a>
            </li>
            <li>
              <a href="/qualify-for-free-vending-machine" className="hover:text-safety">
                Qualify for Free
              </a>
            </li>
            <li>
              <a href="/vending-machine-cost" className="hover:text-safety">
                Vending Machine Cost
              </a>
            </li>

            <li>
              <Link to="/coverage" className="hover:text-safety">
                Coverage Map
              </Link>
            </li>
            <li>
              <Link to="/vending-machines" className="hover:text-safety">
                Machines
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-safety">
                Contact / Apply
              </Link>
            </li>
            <li>
              <Link to="/terms" className="hover:text-safety">
                Terms
              </Link>
            </li>
          </ul>

          <h2 className="mt-10 font-display text-2xl uppercase">States & Cities</h2>
          <div className="mt-4 space-y-6">
            {sorted.map((s) => (
              <div key={s.code} className="border-b border-border pb-4">
                <h3 className="font-semibold">
                  <Link
                    to="/states/$state"
                    params={{ state: s.slug }}
                    className="hover:text-safety"
                  >
                    {s.name}
                  </Link>
                </h3>
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  {s.cities.map((city) => (
                    <li key={city}>
                      <Link
                        to="/states/$state/$town"
                        params={{ state: s.slug, town: slugify(city) }}
                        className="hover:text-safety"
                      >
                        {city}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
